import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  requiredMark?: boolean;
  leadingIcon?: string;
  trailing?: ReactNode;
  ltr?: boolean;
  containerClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    requiredMark,
    leadingIcon,
    trailing,
    ltr,
    className,
    containerClassName,
    id,
    ...rest
  },
  ref,
) {
  return (
    <div className={cn('w-full', containerClassName)}>
      {label ? (
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-foreground-800">
          {label}
          {requiredMark ? <span className="text-rose-600"> *</span> : null}
        </label>
      ) : null}
      <div className="relative">
        {leadingIcon ? (
          <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center justify-center text-foreground-400">
            <i className={cn(leadingIcon, 'text-lg leading-none')} aria-hidden="true" />
          </span>
        ) : null}
        <input
          id={id}
          ref={ref}
          aria-invalid={Boolean(error)}
          className={cn(
            'h-11 w-full rounded-md border bg-background-50 px-3.5 text-sm text-foreground-950 placeholder:text-foreground-400 transition-colors duration-200 ease-smooth',
            leadingIcon && 'ps-10',
            trailing && 'pe-11',
            leadingIcon && 'lb-field-leading',
            trailing && 'lb-field-trailing',
            ltr && 'lb-ltr-field',
            error
              ? 'border-rose-300 focus:border-rose-400'
              : 'border-background-300 hover:border-background-400 focus:border-accent-400',
            'focus:outline-none focus:ring-4 focus:ring-accent-200/60',
            'disabled:cursor-not-allowed disabled:bg-background-100 disabled:text-foreground-400',
            className,
          )}
          {...rest}
        />
        {trailing ? (
          <div className="absolute inset-y-0 end-1.5 flex items-center">{trailing}</div>
        ) : null}
      </div>
      {error ? (
        <p className="mt-1 text-xs text-rose-600">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-foreground-500">{hint}</p>
      ) : null}
    </div>
  );
});

export default Input;